/**
 * Context d'authentification
 */

import { createContext, useContext, useState, useEffect, useRef } from 'react';
import * as authService from '../services/auth.service.js';
import * as geolocationService from '../services/geolocation.service.js';
import { useGeolocation } from '../hooks/useGeolocation.js';
import { isAuthError, isNetworkError } from '../utils/networkError.js';

// Clé sessionStorage : une capture GPS de login par session navigateur
const LOGIN_GEO_CAPTURED_KEY = 'loginGeoCaptured';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { captureSilent, getPermissionState } = useGeolocation();
  const loginGeoRequestedRef = useRef(false);
  // Incrémenté à chaque logout : invalide toute capture en vol (évite d'écrire
  // la position d'un compte A sur un compte B, ou un POST après logout dont le
  // 401 forcerait une redirection — course réelle du flux « compte en attente »).
  const loginGeoGenerationRef = useRef(0);

  // Rafraîchit silencieusement la « dernière position connue »
  // (users.latitude/longitude, lue par tous les consommateurs backend) à chaque
  // connexion ou restauration de session (PRD §6.1.7 — zéro friction).
  // Ne déclenche JAMAIS le prompt système de permission au login (refus sticky,
  // audience non technophile) : GPS seulement si déjà accordé, sinon plan B
  // serveur (géocodage de l'adresse déclarée, puis fallback IP).
  // Fire-and-forget : jamais d'attente, jamais d'erreur affichée, jamais de
  // coordonnées loguées. Les coords hors Sénégal sont rejetées par le backend
  // (diaspora) et ignorées ici.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (loginGeoRequestedRef.current) return;
    const flagKey = `${LOGIN_GEO_CAPTURED_KEY}:${user.id}`;
    if (sessionStorage.getItem(flagKey) === '1') {
      loginGeoRequestedRef.current = true;
      return;
    }
    loginGeoRequestedRef.current = true;
    const generation = ++loginGeoGenerationRef.current;
    (async () => {
      let ok = false;
      const permission = await getPermissionState();
      if (permission === 'granted') {
        const coords = await captureSilent();
        if (coords && loginGeoGenerationRef.current === generation) {
          ok = await geolocationService.captureMyLocation(coords);
        }
      } else if (loginGeoGenerationRef.current === generation) {
        ok = await geolocationService.captureMyLocationFallback();
      }
      if (loginGeoGenerationRef.current !== generation) return; // logout/login entre-temps
      if (ok) {
        sessionStorage.setItem(flagKey, '1');
      } else {
        // Échec (GPS refusé/timeout, réseau) : réessayer à la prochaine
        // session ou au prochain changement d'état auth — le flag de succès
        // n'est posé qu'après une capture effective.
        loginGeoRequestedRef.current = false;
      }
    })().catch(() => {
      if (loginGeoGenerationRef.current === generation) {
        loginGeoRequestedRef.current = false;
      }
    });
  }, [isAuthenticated, user, captureSilent, getPermissionState]);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const storedUser = authService.getStoredUser();
      const token = authService.getToken();
      
      if (token && storedUser) {
        try {
          const response = await authService.getMe();
          if (response.success) {
            setUser(response.data);
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(response.data));
          }
        } catch (error) {
          if (isNetworkError(error)) {
            setUser(storedUser);
            setIsAuthenticated(true);
          } else if (isAuthError(error)) {
            try {
              await authService.refreshToken();
              const retry = await authService.getMe();
              if (retry.success) {
                setUser(retry.data);
                setIsAuthenticated(true);
                localStorage.setItem('user', JSON.stringify(retry.data));
              } else {
                authService.logout();
                setUser(null);
                setIsAuthenticated(false);
              }
            } catch {
              authService.logout();
              setUser(null);
              setIsAuthenticated(false);
            }
          } else {
            console.warn('Erreur getMe au démarrage:', error);
            setUser(storedUser);
            setIsAuthenticated(true);
          }
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (telephone, password) => {
    try {
      const response = await authService.login(telephone, password);
      if (response.success && response.data) {
        const userData = response.data.user || authService.getStoredUser();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Verify the token is valid by calling getMe (with retry on 500 error)
        try {
          const meResponse = await authService.getMe();
          if (meResponse.success) {
            // Update user with latest data from server
            setUser(meResponse.data);
            localStorage.setItem('user', JSON.stringify(meResponse.data));
          }
        } catch (meError) {
          // If getMe fails with 500, it might be a temporary server issue
          // The token is still valid, so we continue with the login
          if (meError.response?.status !== 500) {
            console.warn('Erreur lors de la vérification du token après connexion:', meError);
          }
        }
        
        return { success: true, data: response.data };
      }
      return { success: false, error: response.error || 'Erreur de connexion' };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Erreur de connexion';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      if (response.success && response.data) {
        // Version 3.4: Stocker le token et les infos utilisateur après inscription réussie
        // Cela permet à l'utilisateur d'être automatiquement connecté après inscription
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
          if (response.data.refreshToken) {
            localStorage.setItem('refreshToken', response.data.refreshToken);
          }
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setUser(response.data.user);
            setIsAuthenticated(true);
          }
          try {
            const meResponse = await authService.getMe();
            if (meResponse.success) {
              setUser(meResponse.data);
              localStorage.setItem('user', JSON.stringify(meResponse.data));
            }
          } catch {
            // keep minimal user from register response
          }
        }
        return { success: true, data: response.data };
      }
      return {
        success: false,
        error: response.error || 'Erreur d\'inscription',
        error_code: response.error_code || null,
        details: response.details || null,
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Erreur d\'inscription';
      const errorDetails = error.response?.data?.details || null;
      return {
        success: false,
        error: errorMessage,
        // Code machine (ACCOUNT_EXISTS / INVITATION_USED) pour un routage fiable.
        error_code: error.response?.data?.error_code || null,
        details: errorDetails,
      };
    }
  };

  const logout = (options = {}) => {
    authService.logout();
    // Invalide toute capture en vol et ré-arme pour la prochaine connexion
    loginGeoGenerationRef.current += 1;
    loginGeoRequestedRef.current = false;
    if (user?.id) {
      sessionStorage.removeItem(`${LOGIN_GEO_CAPTURED_KEY}:${user.id}`);
    }
    if (options.skipRedirect) {
      setUser(null);
      setIsAuthenticated(false);
      return;
    }
    const target = options.reason === 'idle' ? '/?session_expired=1' : '/';
    // Hard redirect without clearing React auth state first — avoids ProtectedRoute
    // flashing /login on protected pages before navigation completes.
    window.location.replace(target);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
